import React, { useEffect, useMemo, useState } from "react";
import { Select, Spin } from "antd";
import debounce from "lodash/debounce";
import templateContentService from "../../services/template-content-service";

/**
 * NMU-470: searchable picker over the Content module's pages.
 *
 * The menu editor already had this control inline; a service link now needs the
 * same one, so it lives here instead of being copied. The options are the CMS
 * pages (nmu_m_template) and the value is a templateId.
 *
 * The current value is resolved separately from the search results: when an
 * existing record is opened, its page is almost never in the first page of
 * results, and without this the field would render a bare id instead of the
 * page's name.
 */
const PAGE_SIZE = 50;

const ContentSelect = ({ value, onChange, placeholder, disabled }) => {
    const [options, setOptions] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState(null);

    const search = async (keyword) => {
        setFetching(true);
        try {
            const result = await templateContentService.search(
                { title: keyword || null },
                { currentPage: 1, rowPerPage: PAGE_SIZE, rowCount: 0 },
            );
            setOptions(
                (result?.list ?? []).map((item) => ({
                    value: item.templateId,
                    label: item.mainTitle?.titleTH || item.mainTitle?.titleEN || item.templateId,
                })),
            );
        } catch (err) {
            // A failed lookup must not take the whole dialog down; the field just
            // shows no suggestions and the editor can retype.
            setOptions([]);
        } finally {
            setFetching(false);
        }
    };

    const debouncedSearch = useMemo(() => debounce(search, 500), []);

    useEffect(() => {
        search(null);
        return () => debouncedSearch.cancel();
    }, []);

    // Resolve the saved value's title so editing an existing record shows a name.
    useEffect(() => {
        let cancelled = false;
        if (!value) {
            setSelectedLabel(null);
            return undefined;
        }
        const known = options.find((o) => o.value === value);
        if (known) {
            setSelectedLabel(known.label);
            return undefined;
        }
        (async () => {
            try {
                const resp = await templateContentService.getById({ id: value });
                const bean = resp?.bean;
                if (!cancelled && bean) {
                    setSelectedLabel(bean.mainTitle?.titleTH || bean.mainTitle?.titleEN || value);
                }
            } catch (err) {
                if (!cancelled) setSelectedLabel(null); // fall back to showing the raw id
            }
        })();
        return () => { cancelled = true; };
    }, [value, options]);

    const merged = value && !options.some((o) => o.value === value)
        ? [{ value, label: selectedLabel || value }, ...options]
        : options;

    return (
        <Select
            showSearch
            allowClear
            disabled={disabled}
            placeholder={placeholder}
            value={value || undefined}
            onChange={onChange}
            onSearch={debouncedSearch}
            filterOption={false}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={merged}
        />
    );
};

export default ContentSelect;
